import { parseRelMePubkeys, resolveNip05 } from '@/lib/discoverWebAuthor';
import { fetchTextResilient } from '@/lib/resilientFetch';

/**
 * Verify a claimed author (`p` tag) for a web-sourced highlight.
 *
 * Author tags on URL highlights are *claims* — anyone can credit anyone.
 * Payment must only follow independently verifiable evidence:
 *
 * - `rel-me`: the page itself declares the pubkey (`rel="me"` link). Only
 *   controllable by whoever controls the page. If the page declares a
 *   *different* identity, the claim is actively contradicted.
 * - `domain`: the claimed author's NIP-05 domain matches the page host, and
 *   the domain's nostr.json maps back to the pubkey (two-way handshake —
 *   the domain vouches for the key).
 * - `unverified`: no evidence. Never pay an unverified claim.
 */

export type WebAuthorVerification = 'rel-me' | 'domain' | 'unverified';

export async function verifyWebAuthor(
  url: string,
  claimedPubkey: string,
  claimedNip05?: string,
): Promise<WebAuthorVerification> {
  // 1. Page-declared identity.
  const html = await fetchTextResilient(url);
  if (html) {
    const declared = parseRelMePubkeys(html);
    if (declared.includes(claimedPubkey)) return 'rel-me';
    // The page declares someone else — claim contradicted, stop here.
    if (declared.length > 0) return 'unverified';
  }

  // 2. NIP-05 domain handshake.
  if (claimedNip05) {
    try {
      const host = new URL(url).hostname.toLowerCase();
      const nip05Domain = claimedNip05.split('@')[1]?.toLowerCase();

      // The page host must be the nip05 domain or a subdomain of it
      // (www.example.com ↔ name@example.com).
      if (nip05Domain && (host === nip05Domain || host.endsWith('.' + nip05Domain))) {
        const resolved = await resolveNip05(claimedNip05);
        if (resolved === claimedPubkey) return 'domain';
      }
    } catch {
      // Unparseable URL — fall through.
    }
  }

  return 'unverified';
}
